import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { ArrowUpRight } from 'lucide-react';
import type {
  AnchorHTMLAttributes,
  MouseEventHandler,
  MouseEvent,
  ReactNode,
} from 'react';
import { useCallback, forwardRef } from 'react';

import history from '@/history';
import { getAccountContextForHistory } from '@routes/utils';
import { useAppSelector } from '@store/hooks';
import { selectAccountContextFromAccount } from '@store/reducers/reducer-auth';

import s from './link.scss';

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  children: ReactNode;
  className?: string;
  disabled?: boolean;

  /**
   * When `true`, the link opens in a new tab (`target="_blank"`,
   * `rel="noopener noreferrer"`) and renders a trailing arrow icon. Browser
   * default navigation is used instead of `history.push`.
   */
  external?: boolean;

  /**
   * When `true`, applies a subtle underline style that inherits the surrounding
   * text colour instead of the standard blue. Useful inside dense UI like popup
   * lists where a bright link would be too visually heavy.
   */
  muted?: boolean;

  onClick?: MouseEventHandler<HTMLAnchorElement>;
  to: string;

  /**
   * When `true`, applies the standard underline link styling with blue colour
   * and hover state. Defaults to `false`.
   */
  underline?: boolean;

  useContext?: boolean;
}

function isLeftClickEvent(event: MouseEvent<HTMLAnchorElement>) {
  return event.button === 0;
}

function isModifiedEvent(event: MouseEvent<HTMLAnchorElement>) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      className,
      children,
      disabled = false,
      external = false,
      muted = false,
      onClick,
      to,
      underline = false,
      useContext = true,
      ...props
    },
    forwardedRef,
  ) => {
    useStyles(s);

    const accountContext = useAppSelector(selectAccountContextFromAccount);

    const context =
      useContext && !external
        ? getAccountContextForHistory(accountContext)
        : '';

    const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        if (onClick) {
          onClick(event);
        }

        // Let the browser handle external links, modified clicks, and
        // non-left-click events natively.
        if (external || isModifiedEvent(event) || !isLeftClickEvent(event)) {
          return;
        }

        if (event.defaultPrevented === true) {
          return;
        }

        event.preventDefault();
        history?.push(`${context}${to}`);
      },
      [onClick, to, context, disabled, external],
    );

    return (
      <a
        ref={forwardedRef}
        className={classNames(className, {
          [s.muted]: muted,
          [s.underline]: underline,
        })}
        href={`${context}${to}`}
        {...(external && {
          rel: 'noopener noreferrer',
          target: '_blank',
        })}
        {...props}
        onClick={handleClick}
      >
        {children}
        {external && <ArrowUpRight />}
      </a>
    );
  },
);
Link.displayName = 'Link';

export default Link;
